import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RowPeekDrawer } from '../components/shared/RowPeekDrawer';
import { RowPeekNavLink } from '../components/shared/RowPeekNavLink';
import { formatNumber } from '../lib/utils';
import type { Provider } from '../types/provider';
import { SectionNav } from '../components/layout/SectionNav';
import { CewSiteFooter } from '../components/layout/CewSiteFooter';
import { ErrorState } from '../components/shared/ErrorState';
import { LoadingState } from '../components/shared/LoadingState';
import { TableSourceNote } from '../components/shared/TableSourceNote';
// IntroModal + INTRO_COPY removed 2026-07-02 (Georgetown feedback).
import { ProvidersFilterBar } from '../components/providers/ProvidersFilterBar';
import { ProvidersTableView } from '../components/providers/ProvidersTableView';
import { useProviders } from '../hooks/useProviders';
import { useProvidersOgCounts } from '../hooks/useProvidersOgCounts';
import { useProvidersMetroCounts } from '../hooks/useProvidersMetroCounts';
import { useMetroData } from '../hooks/useMetroData';
import { useUrlParamSeed } from '../lib/use-url-param-seed';
import { useProvidersSelectionStore } from '../stores/providersSelectionStore';
import { lookupSectorByChipId } from '../lib/occupation-utils';
import { GapCoverageCard } from '../components/providers/GapCoverageCard';
import { toCsv, type CsvColumn } from '../lib/csv';

/**
 * CSV columns for the Providers page Copy / Export action.
 *
 * The visible <ProvidersTableView /> swaps one column based on the
 * active OG filter (Maria 2026-06-05) -- the export mirrors that gate
 * so the CSV always matches what the user sees:
 *
 *   - No OG filter:   Name | Metro | Total awards | Springboard
 *                     occupational groups | Programs
 *   - With OG filter: Name | Metro | Total awards | Programs |
 *                     Providers share of credentials aligned with
 *                     springboard occupations
 *
 * "Annual credentials awarded" rounds to integer (the on-screen cell
 * does the same via Math.round). CR-01 (2026-07-07): when an OG
 * filter is active, this column exports the REAL per-group figure
 * (`p.awardsByOccGroup[activeOgName]`) -- matching the on-screen
 * ProviderRow cell -- rather than the institution total; with no
 * filter it exports `p.totalAwards`. The springboard share is
 * rendered as a percent with no decimals to match the row's "N%"
 * formatting; CR-008 exports the ACTIVE group's share
 * (`p.shareSbByOccGroup[activeOgName]`), and a group missing from the
 * map exports an em-dash, again mirroring the table cell. "Programs" exports the
 * COUNT of programs in scope (filtered to the active OG when one is
 * selected; full top-N otherwise) because the column only renders a
 * count -- the program names live in the row-peek modal which the
 * export doesn't surface.
 *
 * Wired 2026-06-09 (Slack Luis + Maria) -- the toolbar previously
 * had no serializer prop and Copy / Export both warn-toasted.
 */
// 2026-07-02 (V2 feedback): CSV column labels mirror the on-screen
// column headers exactly, per the recurring column-name spec.
function providersCsvColumns(activeOgName: string | null): ReadonlyArray<CsvColumn<Provider>> {
  const base: ReadonlyArray<CsvColumn<Provider>> = [
    { label: 'Name', value: (p) => p.name },
    { label: 'Metro area', value: (p) => p.metroName },
    {
      label: 'Annual credentials awarded',
      value: (p) =>
        Math.round(activeOgName ? (p.awardsByOccGroup[activeOgName] ?? 0) : p.totalAwards),
    },
  ];
  if (activeOgName) {
    return [
      ...base,
      {
        label: 'Springboard occupation-aligned programs',
        value: (p) =>
          p.topPrograms.filter((prog) => prog.occGroup === activeOgName).length,
      },
      {
        label:
          "Provider's production share of credentials aligned with springboard occupations",
        // CR-008: export the ACTIVE group's share, mirroring the on-screen
        // cell. activeOgName is non-null in this branch. A group missing
        // from the map (invalid/absent source share) exports an em-dash.
        value: (p) => {
          const raw = p.shareSbByOccGroup[activeOgName];
          return raw == null ? '—' : `${Math.round(raw * 100)}%`;
        },
      },
    ];
  }
  return [
    ...base,
    {
      label: 'Springboard occupational groups',
      value: (p) => p.occupationGroups.length,
    },
    { label: 'Springboard occupation-aligned programs', value: (p) => p.programs.length },
  ];
}

/**
 * Providers page — wired to `/api/providers` (PR B of the DEC-057
 * 2-PR Providers split; PR A landed the backend as #53 / 88d512e).
 *
 * Layout (unchanged from the V1 shell):
 *
 *   <SectionNav>          — sticky top-[var(--h-topbar)] (h-60px)
 *   <header>              — page heading + subtitle, max-w-1200
 *   <ProvidersFilterBar>  — sticky top-[var(--offset-nav-stack)], full-width bg + shadow,
 *                            inner controls aligned to max-w-1200 grid
 *   <section>             — table area (paginated, infinite-scroll)
 *   <CewSiteFooter>
 *
 * Filter set:
 *   1. Search
 *   2. Occupational Group  (chip slug → backend name via lookupSectorByChipId)
 *   3. Metro Area          (populated from useMetroData(), all 55 metros)
 *
 * "Criteria" filter intentionally dropped — no source data per DEC-057
 * diagnostic. Re-introduce in V2 once the desired filter is scoped.
 *
 * Filter state stays page-local (NOT in `filterStore`) because the
 * Providers vocabulary differs from the cross-page Map + Rankings
 * filters and deep-linking is not a current requirement.
 *
 * Pagination uses an IntersectionObserver on a bottom sentinel
 * (rendered inside `<ProvidersTableView>`) with a 200px rootMargin
 * so the next page is requested before the user reaches the end of
 * the list. The hook (`useProviders`) accumulates items across
 * pages within a filter set and resets when filters change.
 */
export default function ProvidersTable() {
  // Filter state -- IMP-006: lives in a zustand singleton so the
  // user's filters survive tab navigation (Maria 2026-06-01). The
  // store mirrors the previous useState shape one-for-one; the page
  // body still reads/writes via the same variable names.
  const search = useProvidersSelectionStore((s) => s.search);
  const setSearch = useProvidersSelectionStore((s) => s.setSearch);
  // chip slug (e.g. "computer-and-mathematical-sciences"); resolved to
  // the full group name before passing to the API.
  const occupationalGroupId = useProvidersSelectionStore((s) => s.occupationalGroupId);
  const setOccupationalGroupId = useProvidersSelectionStore((s) => s.setOccupationalGroupId);
  const metroAreaId = useProvidersSelectionStore((s) => s.metroAreaId);
  const setMetroAreaId = useProvidersSelectionStore((s) => s.setMetroAreaId);

  // Metro options for the dropdown — derived from useMetroData() so
  // the list stays in lockstep with the 55 metros the rest of the
  // tool knows about. Sorted alphabetically by display name.
  const { metros: allMetros } = useMetroData();

  // URL param seeding for the Detail -> Providers cross-tab link
  // (Maria 2026-05-26 ask #2). The Detail page builds
  // `/providers?metroId=X&groupId=Y` and expects the filters to be
  // pre-populated. Both seeds are one-shot per mount via the shared
  // useUrlParamSeed hook (DEC-061) -- once the user opens the page,
  // filter state is their own and subsequent URL changes (e.g.
  // cross-tab nav back into providers) do not stomp it.
  //
  // metroId uses `metros.length > 0` as the guard so we can validate
  // the URL value against the loaded metros list. groupId uses no
  // guard because its validation is delegated to the downstream
  // `lookupSectorByChipId` resolver (unknown chip slugs resolve to
  // null and the filter is treated as inactive).
  useUrlParamSeed('metroId', allMetros.length > 0, (urlMetroId) => {
    if (urlMetroId && allMetros.some((m) => m.id === urlMetroId)) {
      setMetroAreaId(urlMetroId);
    }
  });
  useUrlParamSeed('groupId', true, (urlGroupId) => {
    if (urlGroupId) {
      setOccupationalGroupId(urlGroupId);
    }
  });

  const metroOptions = useMemo(
    () =>
      [...allMetros]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((m) => ({ value: m.id, label: m.name })),
    [allMetros],
  );

  // Selected metro object -- powers the GapCoverageCard summary
  // (Maria 2026-05-26 Slack ask #1). Cheap O(N) lookup over <=55
  // metros; not worth memoizing further than the underlying
  // useMetroData() identity.
  const selectedMetro = allMetros.find((m) => m.id === metroAreaId);

  // Resolve the chip slug → backend group name. The backend matches
  // groupId against entries of Provider.occupationGroups, which carry
  // the raw display name, NOT a slug — see api.ts::ProvidersListParams.
  //
  // IMP-005 -- the resolved OG name (or null when no filter) is also
  // passed down to ProviderRow + the row-peek drawer so the per-row
  // program drill-down filters to the active OG. The resolver returns
  // null both for "no selection" and "chip id not in universe"; we
  // treat both as "no OG filter active" for drill-down purposes.
  const activeOgName = lookupSectorByChipId(occupationalGroupId || null);
  const groupIdForApi = activeOgName ?? '';

  const {
    items,
    total,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    loadMore,
    retry,
  } = useProviders({
    search,
    groupId: groupIdForApi,
    metroId: metroAreaId,
  });

  // Count badges on the OG + Metro dropdowns. Both hooks react to ALL
  // active filters so the badges stay 1:1 with the table results
  // (IMP-017 + 2026-06-05 extension, Maria):
  //   - ogCounts -> per-OG provider count under (metroAreaId, search)
  //   - metroCounts -> per-metro provider count under (groupIdForApi,
  //     search)
  // The hooks are inverses -- each one ignores the dropdown it
  // belongs to so picking an option doesn't immediately zero out
  // every OTHER option's badge (that would be a confusing UX).
  // Errors are swallowed by the hooks; a count fetch failure renders
  // the dropdown without badges (graceful degradation, the filter
  // still works).
  const { counts: ogCounts } = useProvidersOgCounts(metroAreaId, search);
  const { counts: metroCounts } = useProvidersMetroCounts(groupIdForApi, search);

  // Sentinel that drives infinite scroll. Observer is re-bound when
  // hasMore flips or loadMore changes (loadMore is stable across
  // page resolutions — see the ref discipline in useProviders).
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  // Row-peek drawer state. Holds the provider the user clicked;
  // null means the drawer is closed.
  const [peekedProvider, setPeekedProvider] = useState<Provider | null>(null);

  // Use the store's own `reset` action -- it clears all three
  // filters in a single set() so React batches the resulting render.
  // Pulling it from the store keeps the dep array stable (zustand
  // returns the same setter identity across renders).
  const resetFilters = useProvidersSelectionStore((s) => s.reset);
  const clearFilters = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  // CSV serializer for the Copy / Export toolbar in
  // <ProvidersFilterBar>. Pulls from the currently-visible filtered
  // rows (`items`) which already reflect the search + OG + metro
  // filters the user has applied. Column shape is gated by
  // `activeOgName` to mirror the on-screen <ProvidersTableView />
  // (Maria 2026-06-05 column-swap behaviour). Returns empty string
  // when nothing matches so the existing "Nothing to copy" toast
  // fires -- the user gets feedback instead of an empty download.
  const getCsvData = useCallback(() => {
    if (items.length === 0) return '';
    return toCsv(items, providersCsvColumns(activeOgName));
  }, [items, activeOgName]);

  return (
    <>
      <SectionNav />
      {/* Page heading sits above the filter bar in natural flow.
          When the user scrolls down, this heading scrolls out of
          view and the filter bar takes over the top of the viewport. */}
      <div className="mx-auto max-w-page px-8 pt-8">
        <header className="mb-4 flex flex-col gap-2">
          {/* 2026-07-02 (V2 feedback): "education provider" replaced
              with "credential provider" throughout the page — the
              Providers dataset covers institutions that credential
              across the spectrum, not exclusively higher-ed. */}
          <h1 className="text-[clamp(1.25rem,3vw,1.75rem)] font-bold text-navy">
            Find a credential provider
          </h1>
          <p className="text-sm leading-[1.6] text-text-3">
            Search and filter credential providers by occupational group and metro area.
          </p>
        </header>
      </div>
      <ProvidersFilterBar
        search={search}
        onSearchChange={setSearch}
        occupationalGroupId={occupationalGroupId}
        onOccupationalGroupChange={setOccupationalGroupId}
        metroAreaId={metroAreaId}
        onMetroAreaChange={setMetroAreaId}
        metroOptions={metroOptions}
        getCsvData={getCsvData}
        ogCounts={ogCounts}
        metroCounts={metroCounts}
      />
      <div className="bg-[#FCFCFD]">
        <section className="mx-auto flex max-w-page flex-col gap-4 px-8 pb-12 pt-6">
          {isLoading ? (
            <LoadingState label="Loading providers…" />
          ) : error ? (
            <ErrorState
              title="Cannot load providers"
              message={error.message}
              onRetry={retry}
            />
          ) : (
            <>
              {selectedMetro && occupationalGroupId && (
                <GapCoverageCard
                  metro={selectedMetro}
                  groupId={occupationalGroupId}
                  providersInFilterCount={total}
                />
              )}
              <ProvidersTableView
                providers={items}
                total={total}
                isLoadingMore={isLoadingMore}
                hasMore={hasMore}
                sentinelRef={sentinelRef}
                onClearFilters={clearFilters}
                onRowSelect={setPeekedProvider}
                activeOgName={activeOgName}
              />
              {/* 2026-07-02 (V2 feedback): providers-specific
                  source + methodology block. Cites IPEDS only
                  (the other tables also draw on OEWS + ACS +
                  Employment Projections; the Providers page
                  doesn't). */}
              <TableSourceNote variant="providers" />
            </>
          )}
        </section>
      </div>
      <CewSiteFooter />

      {/* Row-peek side-drawer -- click any provider row to surface
          provider stats + cross-tab nav links. Lives outside the
          table section so it overlays the full viewport. */}
      <RowPeekDrawer
        isOpen={peekedProvider !== null}
        onClose={() => setPeekedProvider(null)}
        title={peekedProvider?.name ?? ''}
        eyebrow="CREDENTIAL PROVIDER"
        // 2026-06-05 (Maria): provider peek uses the centered modal
        // variant so the full aligned-program list has room to breathe
        // (the side drawer's 384px column truncated long titles +
        // forced single-column layout). Other tables (Rankings,
        // Compare's metro peek) keep the default 'drawer' variant.
        variant="modal"
      >
        {peekedProvider && (
          <>
            {/* 2026-07-02 (V2 feedback) + 2026-07-03 follow-up:
                summary box labels rewritten per the Georgetown spec
                AND conditionally gated to match the table column
                behavior above:
                  - When looking at "all occupational groups":
                      Springboard occupational groups (count)      -> shown
                      Provider's production share of credentials   -> HIDDEN
                  - When looking at a specific occupational group:
                      Springboard occupational groups (count)      -> HIDDEN
                        (always 1 in a lensed view, no signal)
                      Provider's production share of credentials   -> shown
                CR-01 (2026-07-07): the Total-annual-credentials-awarded
                number now reads the REAL per-group total
                (`peekedProvider.awardsByOccGroup[activeOgName]`) when
                an OG filter is active -- the backend's `awardsByOccGroup`
                field landed, replacing the prior institution-total
                fallback. */}
            <dl className="grid grid-cols-2 gap-3 rounded-lg bg-ice/60 p-4 text-sm">
              <div>
                <dt className="text-eyebrow font-semibold uppercase text-text-4">
                  {activeOgName
                    ? `Annual credentials awarded in ${activeOgName}`
                    : 'Total annual credentials awarded'}
                </dt>
                <dd className="mt-1 font-mono text-base tabular-nums text-text-1">
                  {formatNumber(
                    activeOgName
                      ? Math.round(peekedProvider.awardsByOccGroup[activeOgName] ?? 0)
                      : Math.round(peekedProvider.totalAwards),
                  )}
                </dd>
              </div>
              {activeOgName === null && (
                <div>
                  <dt className="text-eyebrow font-semibold uppercase text-text-4">
                    Springboard occupational groups
                  </dt>
                  <dd className="mt-1 font-mono text-base tabular-nums text-text-1">
                    {peekedProvider.occupationGroups.length}
                  </dd>
                </div>
              )}
              <div className="col-span-2">
                <dt className="text-eyebrow font-semibold uppercase text-text-4">Metro area</dt>
                <dd className="mt-1 text-base text-text-1">{peekedProvider.metroName}</dd>
              </div>
              {activeOgName !== null && (
                <div className="col-span-2">
                  <dt className="text-eyebrow font-semibold uppercase text-text-4">
                    Provider&rsquo;s production share of credentials aligned with springboard occupations
                  </dt>
                  <dd className="mt-1 font-mono text-base tabular-nums text-text-1">
                    {/* CR-008: the active group's share (activeOgName is
                        non-null in this branch), not a collapsed scalar. */}
                    {(() => {
                      const raw = peekedProvider.shareSbByOccGroup[activeOgName];
                      return raw == null ? '—' : `${Math.round(raw * 100)}%`;
                    })()}
                  </dd>
                </div>
              )}
            </dl>

            {(() => {
              // Modal program list:
              //   - With an active OG filter: that group's programs (top-N).
              //   - Without a filter ("All occupational groups"): CR-015 —
              //     the DEDUPLICATED `programs` list (each program once, via
              //     instn_cip_tag), sorted alphabetically (CR-018). Was
              //     `topPrograms`, which duplicated multi-group programs and
              //     truncated at 5/group — the bug Emma flagged.
              //
              // Renders the FULL list (no .slice) since the modal scrolls
              // internally when the list is tall (max ~74 programs).
              const drawerPrograms = activeOgName
                ? peekedProvider.topPrograms.filter((p) => p.occGroup === activeOgName)
                : peekedProvider.programs;
              if (drawerPrograms.length === 0) return null;
              return (
                <div className="rounded-lg border border-border-light p-4">
                  <div className="mb-3 flex items-baseline justify-between gap-2">
                    <p className="text-eyebrow font-semibold uppercase text-text-4">
                      {activeOgName
                        ? `Programs aligned with ${activeOgName}`
                        : 'Springboard occupation-aligned programs'}
                    </p>
                    <p className="text-[11px] font-medium text-text-4">
                      {drawerPrograms.length}{' '}
                      {drawerPrograms.length === 1 ? 'program' : 'programs'}
                    </p>
                  </div>
                  {/* 2026-07-03 (V2 feedback follow-up): the numbers on
                      the right of each row used to be unlabeled --
                      readers had to infer that they were annual awards.
                      A tiny column-title row above the list makes that
                      explicit without competing with the section
                      eyebrow. */}
                  <div className="mb-1.5 flex items-baseline justify-between gap-3 border-b border-border-light pb-1 text-[10px] font-semibold uppercase tracking-wider text-text-4">
                    <span>Program</span>
                    <span className="shrink-0">Annual credentials awarded</span>
                  </div>
                  <ul className="flex flex-col gap-1.5 text-sm text-text-2">
                    {drawerPrograms.map((prog) => (
                      <li
                        key={prog.cipcode}
                        className="flex items-baseline justify-between gap-3 border-b border-border-light pb-1.5 last:border-b-0 last:pb-0"
                      >
                        <span className="min-w-0 break-words leading-snug">
                          {prog.title}
                        </span>
                        <span className="shrink-0 font-mono tabular-nums text-text-4">
                          {formatNumber(Math.round(prog.awards))}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}

            <nav aria-label="Cross-tab navigation" className="flex flex-col gap-1">
              <p className="px-3 pb-1 text-eyebrow font-semibold uppercase text-text-4">
                Explore this metro
              </p>
              <RowPeekNavLink to={`/map?metroId=${peekedProvider.metroId}`}>
                Find this metro on map
              </RowPeekNavLink>
              <RowPeekNavLink to={`/detail?metroId=${peekedProvider.metroId}`}>
                View metro detail
              </RowPeekNavLink>
              <RowPeekNavLink to={`/providers?metroId=${peekedProvider.metroId}`}>
                Other providers in this metro
              </RowPeekNavLink>
            </nav>
          </>
        )}
      </RowPeekDrawer>

    </>
  );
}
